import { computed } from 'vue';
import store from '@/store';
import { toWmsFlag } from './store';

// a request that never answers must not block navigation
const LOAD_TIMEOUT_MS = 10000;

let loadPromise: Promise<boolean> | null = null;
let loadedForToken: string | null = null;

export const wmsManagesProducts = (): boolean => store.getters.getWmsManagesProducts;

// Fetch the setting once per login (cached promise; fetched again when the token changes). The query
// is in the authenticated schema, so without a token we keep the current value (default ON). Called
// by the router guard on every logged-in navigation, so it also runs right after logging in.
export const loadWmsSetting = (force = false): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) {
        return Promise.resolve(wmsManagesProducts());
    }
    if (!loadPromise || force || token !== loadedForToken) {
        loadedForToken = token;
        const fetch = store.dispatch('fetchWmsSetting');
        const timeout = new Promise((resolve, reject) => setTimeout(() => reject(new Error('timeout')), LOAD_TIMEOUT_MS));
        const current = (loadPromise = Promise.race([fetch, timeout]).catch(() => {
            // keep the current value (default ON); try again on the next call
            if (loadPromise === current) {
                loadPromise = null;
            }
            return wmsManagesProducts();
        }) as Promise<boolean>);
    }
    return loadPromise;
};

// set the flag directly (e.g. from the update_system_status response on the settings page)
export const setWmsManagesProducts = (status: unknown): void => {
    store.commit('SET_WMS_MANAGES_PRODUCTS', toWmsFlag(status));
};

// Route meta for screens of data Smart manages (products, categories, units, product groups, price groups):
// - `wmsReadOnly: true`: view-only while WMS is ON (the guard waits for the setting, so the first render is right)
// - `wmsAddRedirect: '<route name>'`: an add page; while WMS is ON the guard sends it to that route (its list)
// Returns the route name to redirect to, or null to open the route.
export const wmsRouteRedirect = async (meta: Record<string, unknown>): Promise<string | null> => {
    if (!meta.wmsReadOnly && !meta.wmsAddRedirect) {
        return null;
    }
    await loadWmsSetting();
    return meta.wmsAddRedirect && wmsManagesProducts() ? String(meta.wmsAddRedirect) : null;
};

// for components (menu, product partials)
export const useWms = () => {
    return {
        wmsManagesProducts: computed(() => wmsManagesProducts())
    };
};

// for screens of data Smart manages: no add / save / delete / sort while WMS manages products (mode only)
export const useWmsReadOnly = () => {
    return {
        readOnly: computed(() => wmsManagesProducts())
    };
};

// Smart id of a synced record (product, category, unit, product group, price group = Smart price list)
const WMS_ID_FIELDS = ['wms_product_id', 'wms_category_id', 'wms_unit_id', 'wms_product_group_id', 'wms_price_list_id'];

// true when the record came from Smart WMS
export const isWmsRecord = (record: unknown): boolean => {
    if (!record || typeof record !== 'object') {
        return false;
    }
    const fields = record as Record<string, unknown>;
    return WMS_ID_FIELDS.some(field => fields[field] != null && fields[field] !== '' && fields[field] !== 0);
};

// one record is view-only while WMS manages products (everything Smart manages) or when it came from Smart (both modes);
// with WMS off, shop records stay editable. Use it per row in lists.
export const wmsRecordReadOnly = (record: unknown): boolean => wmsManagesProducts() || isWmsRecord(record);

// for a page / modal that shows one record: `record` is a ref holding (at least) its wms_*_id
export const useWmsRecordReadOnly = (record: { value: unknown }) => {
    return {
        readOnly: computed(() => wmsRecordReadOnly(record.value))
    };
};

// page header add button of a list of data Smart manages (setPageHeader `actionButton`): none while WMS manages products
export const wmsAddButton = <T>(button: T): T | null => (wmsManagesProducts() ? null : button);
